import { InfoOutlineIcon } from '@chakra-ui/icons';
import {
  Box,
  Text,
  Button,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Flex,
  Input,
  Tooltip,
} from '@chakra-ui/react';
import { Form, Formik, FormikProps } from 'formik';
import React from 'react';

import {
  stringConversion as commonStringConversion,
  utils as commonUtils,
} from '@finley-technologies/common';
import { CreditAgreement } from '@finley-technologies/finley-types';

import { Select } from 'packages/common-frontend/components/Select';
import { WipConfig } from 'packages/creditAgreements/ui/types';

type GlobalRecurringDateConfig = CreditAgreement.Config.GlobalRecurringDate.Config;

export const ReportingEditGlobalRecurringDatePropertyModal: React.FC<{
  isOpen: boolean;
  onCancel: () => void;
  onSave: (
    dateConfig: GlobalRecurringDateConfig,
    options?: { ignoreRecurringDateSetupValidate?: boolean },
  ) => void;
  recurringDateConfig: GlobalRecurringDateConfig;
  wipConfig?: WipConfig;
}> = ({ isOpen, onCancel, onSave, recurringDateConfig }) => {
  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        onCancel();
      }}
      size="xl"
    >
      <ModalOverlay />
      <ModalContent p="5">
        <ModalHeader>
          Edit Recurring Date Config: &quot;{recurringDateConfig.field}&quot;
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody>
          <Formik
            initialValues={{ ...recurringDateConfig }}
            onSubmit={(props) => {
              // Ignoring Recurring Date Config "setup" attribute since just
              // focusing on updating the other attributes in this scope
              onSave(props, { ignoreRecurringDateSetupValidate: true });
              onCancel();
            }}
          >
            {(formProps: FormikProps<GlobalRecurringDateConfig>) => (
              <Form onSubmit={formProps.handleSubmit}>
                <Flex mb="7" alignItems="center">
                  <Flex w="52" alignItems="center">
                    <Text mr="3">Field</Text>
                    <Tooltip
                      label="This attribute represents the name of this config, which
                    other variables and output setup will use to reference"
                    >
                      <InfoOutlineIcon />
                    </Tooltip>
                  </Flex>

                  <Input
                    type="text"
                    name="field"
                    value={formProps.values.field}
                    onChange={formProps.handleChange}
                    onBlur={formProps.handleBlur}
                  />
                </Flex>

                <Box mb="7">
                  <Flex mb="1" alignItems="center">
                    <Flex w="52" alignItems="center">
                      <Text mr="3">Max Count</Text>
                      <Tooltip
                        label='This attribute helps determine when to rely on below
                    "Default Settings", in case the dynamic output is
                    actually fewer than expected'
                      >
                        <InfoOutlineIcon />
                      </Tooltip>
                    </Flex>

                    <Input
                      type="number"
                      name="maxCount"
                      value={formProps.values.maxCount}
                      onChange={formProps.handleChange}
                      onBlur={formProps.handleBlur}
                    />
                  </Flex>
                </Box>

                <Box mb="7">
                  <Text mb="2" fontWeight="semibold">
                    Default Settings
                  </Text>

                  <Flex mb="3" alignItems="center">
                    <Flex w="52" alignItems="center">
                      <Text mr="3">Value</Text>
                      <Tooltip
                        label="If this config does not output enough dates, this value is
                          what the variable will fallback to. By default, will use empty string"
                      >
                        <InfoOutlineIcon />
                      </Tooltip>
                    </Flex>

                    <Input
                      type="text"
                      name="defaultTo.value"
                      value={formProps.values.defaultTo.value}
                      onChange={(evt) => {
                        formProps.setFieldValue(
                          'defaultTo.value',
                          String(evt.target.value ?? ''),
                        );
                      }}
                    />
                  </Flex>

                  <Flex mb="7" alignItems="center">
                    <Flex w="36" alignItems="center">
                      <Text mr="3">Parse String</Text>
                      <Tooltip label='Above value is always read as a "string", so this drives if parsing to another type should be attempted'>
                        <InfoOutlineIcon />
                      </Tooltip>
                    </Flex>

                    <Select
                      value={commonUtils.toInitialCase(
                        String(formProps.values.defaultTo.parseString),
                      )}
                      onChange={(val) => {
                        if (val === undefined) {
                          return;
                        }
                        const booleanVal = commonStringConversion.convertStringBoolean(
                          val,
                        );
                        formProps.setFieldValue(
                          'defaultTo.parseString',
                          booleanVal,
                        );
                      }}
                      options={['False', 'True'].map((value) => ({
                        label: value,
                        value,
                      }))}
                    />
                  </Flex>
                </Box>

                <Flex justifyContent="center">
                  <Button type="submit" mr="2">
                    Done
                  </Button>
                  <Button onClick={() => onCancel()}>Cancel</Button>
                </Flex>
              </Form>
            )}
          </Formik>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
